import {
  ArrowRight,
  BookOpen,
  BriefcaseBusiness,
  Building2,
  CheckCircle2,
  ClipboardCheck,
  Hammer,
  MapPin,
  ShieldCheck,
  Sparkles,
  Wrench,
} from 'lucide-react';

const problems = [
  {
    problem: 'Important commitments live in email, texts, and memory.',
    change: 'One visible record of what was promised, to whom, and by when—owned by a person, not buried in a thread.',
  },
  {
    problem: 'The field and office are working from different versions of the job.',
    change: 'One current version of the job status both sides work from, updated as conditions change instead of reconciled after the fact.',
  },
  {
    problem: 'Meetings create discussion without reliable follow-through.',
    change: 'Meeting decisions become owned, dated actions tied to the plan—not just notes nobody reopens.',
  },
  {
    problem: 'Software was purchased, but the work still depends on spreadsheets and heroics.',
    change: 'Fix the handoffs and ownership first, then configure the tools you already own to run that system—before considering another platform.',
  },
  {
    problem: 'The owner remains the escalation system for nearly everything.',
    change: 'Clear ownership and escalation paths, so problems get handled by the person actually responsible instead of defaulting back to you.',
  },
  {
    problem: 'Problems become visible after they have already affected margin or customers.',
    change: 'Earlier visibility into commitments at risk, so issues surface while there is still time to act—not after the damage is done.',
  },
];

const services = [
  {
    icon: ClipboardCheck,
    title: 'Business Walkthrough',
    text: 'A bounded review of how work, information, commitments, and decisions move through the company. You receive a practical control plan and a prioritized first set of improvements.',
  },
  {
    icon: Wrench,
    title: 'Operating System Build',
    text: 'A focused engagement to improve handoffs, weekly operating rhythm, decision ownership, reporting, and technology or AI workflows only where they remove real work without adding unnecessary bureaucracy.',
  },
  {
    icon: Building2,
    title: 'Ongoing Operating Partner',
    text: 'Monthly support to maintain the system, review emerging issues, improve visibility, and decide where technology or AI should be introduced next.',
  },
];

const segments = [
  {
    title: 'Trade contractors',
    hook: 'Electrical, mechanical, plumbing, concrete—where the field and office are working from different versions of the job.',
    href: '/trade-contractors',
  },
  {
    title: 'Industrial and energy-sector service',
    hook: 'Maintenance, inspection, and fabrication crews coordinating around critical equipment where mistakes are expensive.',
    href: '/industrial-energy-service',
  },
  {
    title: 'Growing multi-location operations',
    hook: 'Companies that won more work than their current systems can carry, and need the operating system to catch up.',
    href: '/scaling-operations',
  },
];

const proofPoints = [
  ['30+', 'years across construction, business ownership, and complex delivery'],
  ['USMC', 'United States Marine veteran'],
  ['Author', 'of The Sentient PMO; developing Forensic Capital Governance'],
  ['100s', 'of detailed articles, field notes, and project narratives'],
  ['Working', 'prototypes, simulations, workshops, and digital products'],
  ['Houston', 'based and focused on local owners and operators'],
];

const experienceAreas = [
  {
    icon: Hammer,
    title: 'Construction and project controls',
    text: 'Michael began his career in construction and project controls after earning a Bachelor of Science from the University of Florida. He worked as a cost control engineer and estimator with Ralph M. Parsons and served as Quality Control Supervisory Engineer on a U.S. Army Corps of Engineers environmental-remediation program at the former Hamilton Air Force Base. He later became Master Scheduler on the Pentagon Renovation Program and led complex physical-infrastructure delivery including the TD Ameritrade thinkorswim trading-floor expansion. That foundation shaped his approach to schedule, cost, dependencies, change, evidence, and field-to-office coordination.',
  },
  {
    icon: BriefcaseBusiness,
    title: 'Entrepreneurship and business ownership',
    text: 'He has built businesses, products, workshops, websites, simulations, and service concepts from the ground up. He understands that ambiguity, rework, delayed decisions, and weak follow-through affect the owner directly.',
  },
  {
    icon: Building2,
    title: 'Enterprise and regulated operations',
    text: 'Michael’s enterprise career moved into financial services in 1997 and later included leading the design and delivery of a custom Morgan Stanley portfolio-management capability representing roughly 500 projects and $1B in annual project spend. His work since has spanned financial services, healthcare, utilities, infrastructure, Professional Services, cybersecurity and IAM, governance, compliance, vendor integration, and regulated operating environments where weak handoffs become expensive quickly.',
  },
  {
    icon: Sparkles,
    title: 'Governed AI-assisted work',
    text: 'Michael’s current work uses AI-assisted research, synthesis, evidence organization, prototyping, testing, and review under controlled source-of-record and human-approval practices. The objective is practical: remove administrative work and improve visibility without transferring judgment or accountability to the machine.',
  },
];

const clientGroups = [
  {
    category: 'Financial services & insurance',
    clients: ['JPMorgan Chase', 'Citi', 'Morgan Stanley', 'PNC', 'TD Ameritrade', 'MetLife', 'Guardian Life'],
  },
  {
    category: 'Healthcare & public sector',
    clients: ['Smith+Nephew', 'Methodist Hospital', 'NYC Health + Hospitals', 'Fannie Mae'],
  },
  {
    category: 'Energy, industrial & technology',
    clients: ['Ameren', 'VMware', 'Illumio', 'The Home Depot'],
  },
];

const projectStories = [
  {
    label: 'Environmental remediation, Hamilton Air Force Base',
    title: 'The first time my name became part of the control system',
    text: 'Early in his career, Michael served as Quality Control Supervisory Engineer on a U.S. Army Corps of Engineers environmental-remediation program at the former Hamilton Air Force Base—contaminated-soil excavation, demolition, landfill closure, and construction of a treatment facility. He built the schedule, managed reporting, and connected contractual requirements to what was actually happening in the field. The lesson that stuck: a test result or a status update is only as good as the chain connecting it back to what it actually represents.',
    outcome: 'Demonstrates early, hands-on accountability for evidence integrity—an origin point for the evidence-and-authority discipline behind every engagement since.',
    href: 'https://mkaplan.substack.com/p/the-first-time-my-name-became-part',
  },
  {
    label: 'Engineering cost controls, Ralph M. Parsons',
    title: 'Tracking a $750 million plant design across 13 engineering disciplines',
    text: 'Early in his career, Michael worked as a cost control engineer and estimator with Ralph M. Parsons in Pasadena, California, tracking cost, progress, and billable effort on a $750 million engineering design program for a new Exxon Chemical Corporation plant in Baton Rouge. Coordinating more than 13 engineering disciplines on a single design effort taught him early how easily progress and cost can drift out of sync across specialties—and what it takes to keep them reconciled.',
    outcome: 'Demonstrates early, hands-on cost and progress control across a large multidisciplinary program.',
  },
  {
    label: 'Pentagon Renovation Program (PENREN)',
    title: 'From IM&T lead scheduler to Master Scheduler on the Pentagon Renovation Program',
    text: 'From 1995 to 1997, Michael worked for 3D/I on the Pentagon Renovation Program during the early Wedge 1 period, approximately one million square feet of renovation scope in an occupied national-security facility. He progressed from Lead Scheduler for Information Management & Telecommunications to Master Scheduler, proposing, creating, and managing the Master Integrated Schedule that connected construction, telecommunications, swing space, furniture and equipment, MEP, procurement, readiness, and occupancy dependencies.',
    outcome: 'Demonstrates large-scale dependency management and the discipline of keeping major workstreams synchronized against one integrated schedule.',
    href: 'https://mkaplan.substack.com/p/mastering-complexity-scheduling-the',
  },
  {
    label: 'thinkorswim trading floor, TD Ameritrade',
    title: 'Recovering technology work that was three months behind schedule',
    text: 'Michael took over the technology and infrastructure initiative for a 53,000-square-foot expansion of TD Ameritrade’s thinkorswim trading operation after the technology work had fallen three months behind schedule. He established the integrated delivery structure, coordinated an 18-person cross-functional program team, and drove the integrated design to 90% completion before major vendor commitment.',
    outcome: 'Avoided $500,000 in cost and change-order exposure and delivered the integrated design package 45 days ahead of the revised schedule.',
    href: 'https://mkaplan.substack.com/p/architecting-a-live-trading-floor',
  },
  {
    label: 'Governed AI-assisted research and prototyping',
    title: 'Using AI to prepare evidence without surrendering judgment',
    text: 'Through MAK CON, Michael directs AI-assisted research, synthesis, evidence organization, prototyping, testing, and controlled revision across governance methods, books, workshops, and working prototypes. Source-of-record discipline, provenance, review, correction, and human approval remain part of the operating model.',
    outcome: 'Demonstrates practical AI use inside a governed workflow where people retain authority for accepted content, state, and decisions.',
  },
  {
    label: 'Independent build and research',
    title: 'Turning years of experience into working systems',
    text: 'Michael has developed books, websites, workshops, simulations, governance models, content systems, and working prototypes exploring how organizations can see work more clearly and act with better evidence.',
    outcome: 'Demonstrates that the work is built, tested, documented, and continuously refined.',
  },
];

const selectedWork = [
  {
    title: 'The Sentient PMO',
    type: 'Published book and operating-method lineage',
    text: 'A practical exploration of how AI can preserve context, reduce administrative work, and improve delivery visibility without replacing accountable leadership.',
    href: 'https://www.amazon.com/dp/B0G7BCCP8D',
  },
  {
    title: 'Forensic Capital Governance',
    type: 'Book in development and enterprise decision-assurance discipline',
    text: 'A developing body of work on evidence, changing assumptions, continued investment, executive decisions, and the record supporting consequential commitments.',
    href: 'https://forensicgovernance.com',
  },
  {
    title: 'The Sentient Architect',
    type: 'Workshops and simulations',
    text: 'Founder-led workshops, demonstrations, and simulations designed to help leaders experience how AI changes operating work, evidence, and decision systems.',
    href: 'mailto:contact@makconllc.com?subject=The%20Sentient%20Architect',
  },
  {
    title: 'Project narratives and field notes',
    type: 'Long-form writing archive',
    text: 'A substantial writing archive documents the projects, methods, experiments, lessons, and operating problems behind the work. MAK Business Works can draw on that material when it helps an owner understand a practical operating problem.',
    href: 'https://mkaplan.substack.com',
  },
];

export default function App() {
  return (
    <div className="site-shell">
      <header className="site-header">
        <a className="brand" href="#top" aria-label="MAK Business Works home">
          <span className="brand-mark">MAK</span>
          <span className="brand-copy">
            <strong>Business Works</strong>
            <small>A service of MAK CON LLC</small>
          </span>
        </a>
        <nav aria-label="Primary navigation">
          <a href="#problem">The problem</a>
          <a href="#services">Services</a>
          <a href="#about">Michael</a>
          <a href="#proof">Experience</a>
          <a href="#case-studies">Work &amp; writing</a>
        </nav>
        <a className="header-cta" href="mailto:contact@makconllc.com?subject=Business%20Walkthrough">
          Start a conversation
        </a>
      </header>

      <main id="top">
        <section className="hero">
          <div className="hero-copy">
            <p className="eyebrow"><MapPin size={15} /> Houston operating systems for contractors &amp; service companies</p>
            <h1>Build a business that is easier to see, run, and grow.</h1>
            <p className="hero-lead">
              MAK Business Works helps Houston contractors and service companies fix the gaps between the field, the office, the numbers, and the decisions.
            </p>
            <div className="hero-actions">
              <a className="button button-primary" href="mailto:contact@makconllc.com?subject=Business%20Walkthrough">
                Schedule a Business Walkthrough <ArrowRight size={18} />
              </a>
              <a className="button button-secondary" href="#proof">See the experience behind the work</a>
            </div>
            <div className="trust-line">
              <ShieldCheck size={20} />
              <span>Construction-rooted. Marine veteran-led. Built from more than 30 years of operating experience.</span>
            </div>
          </div>

          <aside className="field-card" aria-label="Illustrative operating walkthrough">
            <div className="field-card-topline">
              <span>Monday operating view</span>
              <span>Houston / 07:00</span>
            </div>
            <h2>What should the owner be able to see before the week gets expensive?</h2>
            <dl>
              <div><dt>Commitment at risk</dt><dd>Customer handoff is waiting on an unresolved field condition.</dd></div>
              <div><dt>Information gap</dt><dd>The office schedule does not reflect the latest crew constraint.</dd></div>
              <div><dt>Decision needed</dt><dd>Resequence, add capacity, or reset the customer expectation today.</dd></div>
            </dl>
            <p className="field-note">Better systems do not create more reporting. They make the next responsible action easier to see.</p>
          </aside>
        </section>

        <section id="problem" className="section problem-section">
          <div className="section-heading">
            <p className="eyebrow"><Hammer size={15} /> Growth exposes the operating system</p>
            <h2>Your business may have outgrown the way work moves.</h2>
            <p>You probably do not need another platform. You need to find where commitments disappear, information arrives late, and the business still depends on someone remembering what happens next.</p>
          </div>
          <div className="problem-grid">
            {problems.map(({ problem, change }, index) => (
              <article key={problem}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <p>{problem}</p>
                <div className="problem-change">
                  <CheckCircle2 size={16} />
                  {change}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section id="services" className="section services-section">
          <div className="section-heading compact">
            <p className="eyebrow">How we engage</p>
            <h2>Start with the work as it actually happens.</h2>
          </div>
          <div className="service-grid">
            {services.map(({ icon: Icon, title, text }) => (
              <article key={title} className="service-card">
                <Icon size={28} />
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section segments-section">
          <div className="section-heading compact">
            <p className="eyebrow">Who I work with</p>
            <h2>Built around a few kinds of businesses I understand well.</h2>
          </div>
          <div className="segment-grid">
            {segments.map(({ title, hook, href }) => (
              <article key={title} className="segment-card">
                <h3>{title}</h3>
                <p className="segment-hook">{hook}</p>
                <a href={href}>See how this applies <ArrowRight size={16} /></a>
              </article>
            ))}
          </div>
        </section>

        <section id="about" className="section founder-section">
          <div className="founder-intro">
            <img
              className="founder-photo"
              src="/images/founder-michael-kaplan.jpg"
              alt="Michael Kaplan, founder of MAK Business Works"
              width={240}
              height={240}
            />
            <div>
              <p className="eyebrow">Founder, MAK Business Works · Principal Consultant, MAK CON</p>
              <h2>Michael Kaplan</h2>
              <p className="founder-role">Senior enterprise program leader, operator, author, and United States Marine veteran.</p>
            </div>
          </div>
          <div className="founder-copy">
            <p className="founder-lead">I have spent more than 30 years learning how complex work actually moves—from construction sites and project controls to business ownership, regulated enterprise programs, infrastructure, cybersecurity and IAM, governance, and technology-enabled operations.</p>
            <p>My broader professional work centers on senior enterprise program leadership: PMO and portfolio governance, transformation, program recovery, infrastructure, cybersecurity/IAM, Professional Services, and regulated delivery. MAK Business Works is a focused Houston service of MAK CON that brings those operating disciplines to owners and operators in smaller businesses.</p>
            <p>My perspective began in construction, where incomplete work, missed dependencies, weak handoffs, and unsupported status eventually become impossible to hide. Entrepreneurship added another lesson: operating ambiguity is not theoretical when the owner carries the consequences directly.</p>
            <p>My current research and product work also uses AI where it can reduce administrative burden, organize evidence, accelerate analysis, and improve visibility while keeping judgment and accountability with people.</p>
            <p>MAK Business Works brings that combined experience back to Houston owners who need practical help making the company easier to run without introducing unnecessary bureaucracy.</p>
          </div>
        </section>

        <section id="proof" className="section proof-section">
          <div className="section-heading compact">
            <p className="eyebrow">Experience you can inspect</p>
            <h2>The offer is practical because the experience behind it is broad, documented, and built in real operating environments.</h2>
          </div>
          <div className="proof-grid">
            {proofPoints.map(([value, label]) => (
              <article key={label}>
                <strong>{value}</strong>
                <span>{label}</span>
              </article>
            ))}
          </div>
          <div className="experience-grid">
            {experienceAreas.map(({ icon: Icon, title, text }) => (
              <article key={title}>
                <Icon size={25} />
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section clients-section">
          <div className="section-heading compact">
            <p className="eyebrow">Track record</p>
            <h2>Experience across major organizations in finance, healthcare, energy, technology, and government.</h2>
            <p>These include employers, clients, and customer environments from Michael&apos;s career. Enterprise scale isn&apos;t the offer for Houston owners—it&apos;s the depth of discipline behind it.</p>
          </div>
          <div className="client-groups">
            {clientGroups.map(({ category, clients }) => (
              <article key={category} className="client-group">
                <h3>{category}</h3>
                <p>{clients.join(' · ')}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section project-section">
          <div className="section-heading compact">
            <p className="eyebrow">Representative project stories</p>
            <h2>Selected examples of how the work translates into operating judgment.</h2>
          </div>
          <div className="project-grid">
            {projectStories.map((story, index) => (
              <article key={story.title}>
                <div className="project-number">0{index + 1}</div>
                <p className="project-label">{story.label}</p>
                <h3>{story.title}</h3>
                <p>{story.text}</p>
                <div className="project-outcome"><CheckCircle2 size={18} /> {story.outcome}</div>
                {story.href && (
                  <a className="project-story-link" href={story.href} target="_blank" rel="noreferrer">
                    Read the full story <ArrowRight size={14} />
                  </a>
                )}
              </article>
            ))}
          </div>
        </section>

        <section id="case-studies" className="section work-section">
          <div className="section-heading">
            <p className="eyebrow"><BookOpen size={15} /> Built, written, and documented</p>
            <h2>A substantial body of work sits behind this service.</h2>
            <p>A substantial archive documents projects, experiments, operating lessons, product builds, workshop designs, AI use cases, and governance problems. MAK Business Works draws on the strongest material when it helps an owner understand a practical operating problem.</p>
          </div>

          <div className="selected-work-grid">
            {selectedWork.map((item) => (
              <article key={item.title}>
                <p className="work-type">{item.type}</p>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
                <a href={item.href} target={item.href.startsWith('http') ? '_blank' : undefined} rel={item.href.startsWith('http') ? 'noreferrer' : undefined}>
                  Explore this work <ArrowRight size={16} />
                </a>
              </article>
            ))}
          </div>
        </section>

        <section id="insights" className="section insights-section">
          <div className="section-heading compact">
            <p className="eyebrow">Built to Run</p>
            <h2>A local series is coming. The archive is already here.</h2>
            <p>Built to Run will translate the larger body of work into direct lessons for Houston owners building businesses that cannot depend on memory, heroics, and another spreadsheet. Those pieces are in progress. In the meantime, the full archive of project narratives, field notes, and operating lessons behind this work is available now.</p>
          </div>
          <a className="button button-primary" href="https://mkaplan.substack.com" target="_blank" rel="noreferrer">
            Read the archive on Substack <ArrowRight size={18} />
          </a>
        </section>

        <section className="closing-section">
          <div>
            <p className="eyebrow">Better systems. Fewer surprises.</p>
            <h2>Tell me where the business is becoming difficult to see or control.</h2>
          </div>
          <a className="button button-light" href="mailto:contact@makconllc.com?subject=MAK%20Business%20Works%20Conversation">
            Start a conversation <ArrowRight size={18} />
          </a>
        </section>
      </main>

      <footer>
        <div>
          <strong>MAK Business Works</strong>
          <span>A service of MAK CON LLC</span>
        </div>
        <p>Houston, Texas · Marine veteran-led · Better systems, practical technology, accountable decisions</p>
        <div className="footer-legal-links">
          <a href="mailto:contact@makconllc.com">contact@makconllc.com</a>
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
        </div>
      </footer>
    </div>
  );
}
